import { useEffect, useState } from 'react';
import { EVENTO_ACCESOS } from './eventos';

/**
 * accesos.ts — Favoritos y Recientes: el contrato entre quien los escribe y
 * quien los enseña.
 *
 * Se fijan con la estrella del **menú lateral** (`features/armazon/accesos.ts`)
 * y se enseñan en la tarjeta «Tus accesos» del **Inicio**. Dos features, así que
 * lo compartido vive aquí y no en ninguna de las dos: el nombre del evento, la
 * forma del registro y las dos claves de `localStorage`.
 *
 * Vivió en `features/inicio/accesos.ts` mientras el único que escribía era
 * `app.js` —que no importa de esta capa y tecleaba los nombres por su cuenta—.
 * Al pasar el menú a React el escritor entró aquí dentro y pudo **importar** el
 * nombre del evento en vez de repetirlo. Es la diferencia entre dos literales
 * que hay que acordarse de cambiar a la vez y uno que no se puede escribir mal.
 *
 * ## Cómo se entera Inicio de un cambio
 *
 * `localStorage` no avisa a la misma pestaña que lo escribe: el evento `storage`
 * del navegador solo llega a las **otras**. Por eso quien escribe emite además
 * un evento propio, y esta pantalla lo escucha.
 *
 * Hasta el 2026-09-07 esto era `window.PageDashboard?.refreshQuickAccess?.()`:
 * el shell llamando a una pantalla concreta por su nombre global, con `?.`
 * delante. Al migrar el Inicio a React ese global dejó de existir y la llamada
 * **no dio error** — dejó de refrescar, y la estrella del menú parecía no hacer
 * nada. Un evento no tiene ese problema: nadie tiene que existir para que se
 * emita, y quien escuche se entera sin que el emisor sepa quién es.
 */

/* El nombre del evento vive en `lib/eventos.ts`, con los otros dos que cruzan
   entre las dos raíces. Se reexporta porque es parte de este contrato. */
export { EVENTO_ACCESOS };

export interface AccesoGuardado {
  href: string;
  label: string;
  /** La clase del icono, con `nav-icon` incluida: es lo que hay escrito en el
   *  `localStorage` de quien ya tiene favoritos y lo que usa el CSS de la
   *  tarjeta de Inicio. */
  iconClass: string;
  group?: string;
}

export const CLAVES_DE_ACCESOS = { favoritos: 'nav_favorites', recientes: 'nav_recent' } as const;

export function leerAccesos(clave: string): AccesoGuardado[] {
  try {
    const crudo = JSON.parse(localStorage.getItem(clave) || '[]');
    if (!Array.isArray(crudo)) return [];
    // Se filtra por forma y no por confianza: lo escribe el propio portal, pero
    // `localStorage` lo puede editar cualquiera con la consola abierta, y una
    // entrada a medias rompería el render de la lista entera.
    return crudo.filter(
      (a): a is AccesoGuardado =>
        !!a && typeof a.href === 'string' && typeof a.label === 'string',
    );
  } catch {
    return [];
  }
}

/** Guarda y avisa. Las dos cosas juntas a propósito: guardar sin avisar es el
 *  defecto que esta cabecera cuenta, y no hay ningún caso en que interese. */
export function escribirAccesos(clave: string, lista: AccesoGuardado[]): void {
  Utils.recordar.guardar(clave, lista);
  window.dispatchEvent(new CustomEvent(EVENTO_ACCESOS));
}

/**
 * Favoritos y recientes, al día.
 *
 * Se relee con el evento de arriba y también con `storage`, que cubre el caso de
 * tener el portal abierto en dos pestañas: fijar en una y ver el cambio en la
 * otra al volver.
 */
export function useAccesos() {
  const [accesos, setAccesos] = useState(() => ({
    favoritos: leerAccesos(CLAVES_DE_ACCESOS.favoritos),
    recientes: leerAccesos(CLAVES_DE_ACCESOS.recientes),
  }));

  useEffect(() => {
    const releer = () =>
      setAccesos({
        favoritos: leerAccesos(CLAVES_DE_ACCESOS.favoritos),
        recientes: leerAccesos(CLAVES_DE_ACCESOS.recientes),
      });
    window.addEventListener(EVENTO_ACCESOS, releer);
    window.addEventListener('storage', releer);
    return () => {
      window.removeEventListener(EVENTO_ACCESOS, releer);
      window.removeEventListener('storage', releer);
    };
  }, []);

  return accesos;
}
